import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Button,Box, Card, CardContent, CardMedia, Typography, Grid } from '@mui/material'
import { ValidatorForm } from 'react-material-ui-form-validator'
import { Brand, IEWarningPopup, Alert, StyledModal, AnimatedSuccessTick } from 'frame/components'
import MuiTextfield from 'frame/components/mui-textfield'
import { PasswordIndicator } from 'features/account/components'
import * as fromAuth from 'auth'
import iOSLinkImage from 'assets/apple_app_link.png'
import androidLinkImage from 'assets/android_app_link.png'
import { useProfile } from 'features/account'
import { themeV5 } from 'theme/v5'

const VerificationDriver = ({ onVerify, onClear, verification }) => {
  const form = useRef()
  const disabled = false
  const location = useLocation()
  const rawToken = location.search || ''
  const profile = useProfile()
  const isLoggedIn = !!profile.email && !!profile.role

  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)

  const verifiedSuccess = verification && verification.success && verification.success
  const responseMessage = verification && verification.responseMessage

  useEffect(() => {// on load
    onClear() // clear any previous verification messages onload
    setShowConfirmationModal(false) // incase state is not cleared somehow
    console.log('[islogged in]', !!profile.email)
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    // force other tab sessions to logout if opened in the same browser session
    isLoggedIn && !showLogoutModal && setShowLogoutModal(true)
    // eslint-disable-next-line
  }, [isLoggedIn])

  useEffect(() => {
    verifiedSuccess && setShowConfirmationModal(true)
  }, [verifiedSuccess])

  // redirect from page if verification url expired
  let token = ''
  if (rawToken !== '') {
    token = rawToken.split('=')[1]
    // console.log('token', token)
    if (token.startsWith('=3D') && window.location.href.startsWith('http://localhost:')) {
      token = token.slice(2)
    }
    // console.log('token sliced2', token)
  }

  const isIE = /*@cc_on!@*/false || !!document.documentMode

  const [values, setValues] = useState({
    password: '',
    confirmPassword: '',
  })

  const hasNumber = (value) => /(.*\d.*)/.test(value)
  const hasLowercase = (value) => /(.*[a-z].*)/.test(value)
  const hasUppercase = (value) => /(.*[A-Z].*)/.test(value)
  const hasSymbol = (value) => /[-!$%^&@#*()_+|~=`{}[\]:";'<>?,./]/.test(value)

  useEffect(() => {

    ValidatorForm.addValidationRule('isPasswordMatch', (value) => {
      return value !== values.password ? false : true
    })

    ValidatorForm.addValidationRule('hasNumber', (value) => hasNumber(value))
    ValidatorForm.addValidationRule('hasLowercase', (value) => hasLowercase(value))
    ValidatorForm.addValidationRule('hasUppercase', (value) => hasUppercase(value))
    ValidatorForm.addValidationRule('hasSymbol', (value) => hasSymbol(value))

    return () => {
      ValidatorForm.removeValidationRule('isPasswordMatch')
      ValidatorForm.removeValidationRule('hasNumber')
      ValidatorForm.removeValidationRule('hasLowercase')
      ValidatorForm.removeValidationRule('hasUppercase')
      ValidatorForm.removeValidationRule('hasSymbol')
    }

  }, [values.password])

  const onChange = (key) => ({ target: { value } }) =>
    setValues({
      ...values,
      [key]: value
    })

  const valid = value => value && value !== ''
  const disableSave = ! (valid(values.password)
    && valid(values.confirmPassword)
    && values.password === values.confirmPassword
    && values.password.length > 7
    && hasNumber(values.password)
    && hasLowercase(values.password)
    && hasUppercase(values.password)
    && hasSymbol(values.password))

  const onVerifyHandler = () => {
    setShowLogoutModal(false)
    if (valid(token) && valid(values.confirmPassword))
    {onVerify({
      password: values.confirmPassword,
      token
    })}
  }

  const onLogout = () => {
    // unable to invalidate auth0 token across tabs issue: https://github.com/auth0/auth0-spa-js/issues/136
    // hack: to signal other tabs to force sign out
    isLoggedIn && localStorage.setItem('FORCE_LOGOUT_ALL_SESSIONS', true)
    setShowLogoutModal(false)
  }

  const openIOSLink = () =>
    window.open(process.env.REACT_APP_DRIVER_APP_LINK_IOS, '_blank')

  const openAndroidLink = () =>
    window.open(process.env.REACT_APP_DRIVER_APP_LINK_ANDROID, '_blank')

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingY: '48px',
        paddingX: '16px',
      }}
      title="Login">
      {isIE && <IEWarningPopup />}
      <Card
        sx={{
          width: '960px',
          maxWidth: '100%',
          overflow: 'unset',
          display: 'flex',
          position: 'relative',
          '& > *': {
            flexGrow: 1,
            flexBasis: '50%',
            width: '50%',
          },
          '@media (max-width: 760px)': {
            maxWidth: '400px',
          },
        }}>
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: [3, 4],
            minHeight: 300,
          }}>
          <Box>
            <Box
              sx={{
                display: 'flex',
                paddingBottom: '24px',
                color: themeV5.palette.white,
              }}>
              <Brand />
            </Box>
            {/* {JSON.stringify(values)} */}

            <Typography gutterBottom variant="body1">
              Please enter your password and click verify account to complete your account verification.
            </Typography>

            {/* token: {JSON.stringify(token)} */}

            <ValidatorForm
              autoComplete="off"
              noValidate
              ref={form}
              onSubmit={() => console.log('ValidatorFormOnSubmit clicked')}>
              <Grid paddingTop={4}>
                <Grid xs={12} paddingBottom={2}>
                  <br />
                  <MuiTextfield
                    id="password"
                    disabled={disabled}
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={values.password}
                    onChange={onChange('password')}/>
                </Grid>
                <Grid xs={12} spacing={2} paddingBottom={4}>
                  <MuiTextfield
                    id="confirmPassword"
                    disabled={disabled}
                    fullWidth
                    label="Confirm Password"
                    name="confirmPassword"
                    type="password"
                    value={values.confirmPassword}
                    onChange={onChange('confirmPassword')}/>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">Password Policy:</Typography>
                  <PasswordIndicator
                    passwordMatch={values.password !== '' && values.password === values.confirmPassword}
                    meetsPasswordLength={values.password.length > 7}
                    hasNumber={hasNumber(values.password)}
                    hasLowercase={hasLowercase(values.password)}
                    hasUppercase={hasUppercase(values.password)}
                    hasSymbol={hasSymbol(values.password)}/>
                </Grid>
              </Grid>
            </ValidatorForm>

          </Box>
          <Button
            sx={{
              marginTop: '24px',
              width: '100%',
              textTransform: 'uppercase',
              fontWeight: 'bold',
              fontSize: '16px',
            }}
            fullWidth
            color="error"
            disabled={disableSave}
            type="submit"
            variant="contained"
            onClick={onVerifyHandler}>
            Verify Account
          </Button>
          <br />
          {!verifiedSuccess && responseMessage && (
            <Alert>
              {responseMessage}
            </Alert>
          )}
        </CardContent>
        <CardMedia
          sx={{
            position: 'relative',
            borderTopRightRadius: '32px',
            borderBottomRightRadius: '32px',
            padding: 3,
            color: themeV5.palette.white,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            '@media (max-width: 760px)': {
              display: 'none',
            },
          }}
          image="/static/images/login-image.jpg"
          title="Toyota"/>
      </Card>

      {/* Confirmation Modal */}
      <StyledModal
        open={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirmLabel = "Okay"
        onConfirm={() => setShowConfirmationModal(false)}>
        <>
          <Typography variant="body1" align="center">
            Account has been successfully verified
          </Typography>
          <Box
            sx={{
              marginTop: 2
            }}>
            <AnimatedSuccessTick size={50} />
          </Box>
          <Box
            sx={{
              marginTop: 2
            }}>
            <>
              <img
                style={{
                  display: 'inline-flex',
                  flexWrap: 'wrap',
                  flexDirection: 'row',
                  width: 120,
                  margin: 10,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  '&:hover': {
                    cursor: 'pointer',
                  },
                }}
                src={iOSLinkImage}
                alt="iOS App Link"
                onClick={openIOSLink}/>
              <img
                style={{
                  display: 'inline-flex',
                  flexWrap: 'wrap',
                  flexDirection: 'row',
                  width: 120,
                  margin: 10,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  '&:hover': {
                    cursor: 'pointer',
                  },
                }}
                src={androidLinkImage}
                alt="Android App Link"
                onClick={openAndroidLink}/>
            </>
          </Box>
        </>
      </StyledModal>

      {/* on load check if user is already logged in */}
      <StyledModal
        open={showLogoutModal}
        title={`Looks like you are already logged in`}
        onCancel={onLogout}
        onConfirmLabel="Log Out"
        onConfirm={onLogout}
        options={['noCancelButton']}>
        {`Please log out to continue set password for driver app`}
      </StyledModal>
    </Box>
  )
}

VerificationDriver.propTypes = {
  onVerify: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  verification: PropTypes.object.isRequired,
}

const mapStateToProps = ({ account }) => ({
  verification: account.verification,
})

const mapDispatchToProps = (dispatch) => ({
  onVerify: (payload) =>
    dispatch(fromAuth.verifyDriver(payload)),
  onClear: () =>
    dispatch(fromAuth.verifyDriverClearMessage())
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(VerificationDriver)
